import { useState, useRef, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calculator, Pencil } from "lucide-react";
import { PhotoAnnotator } from "@/components/photos/photo-annotator";
import { PhotoApi } from "@/lib/photo-api";
import { useToast } from "@/hooks/use-toast";

// ──────────────────────────────────────────────────────────────────────────────
// Legacy-Typen — behalten für Rückwärtskompatibilität gespeicherter Datensätze
// und für unveränderte Konsumenten (quote-generator, pdf-generator).
// Die neue UI produziert ausschließlich amount/amountFormula/unit; die alten
// Felder werden nicht mehr neu geschrieben, aber beim Hydrieren noch gelesen.
// ──────────────────────────────────────────────────────────────────────────────
interface ReferencePoint {
  id: string;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  realMeasurement: number;
  description: string;
  pixelLength: number;
  scale: number;
}

interface OpeningArea {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  description: string;
  area: number;
}

export interface ManualMenge {
  id: string;
  label: string;
  unit: string;
  value: number;
  isManual: boolean;
}

export interface MeasurementResultBase {
  /** Optional: PNG/JPEG-Data-URL einer annotierten Variante (Legacy). */
  annotatedDataUrl?: string;
}

export interface MeasurementResult extends MeasurementResultBase {
  photoId: string;
  /** Neues Datenmodell: einheitliches Mengenfeld. */
  amount: number;
  /** Optional: Berechnungsformel zur Menge (z.B. "3*4+1,5*2"). */
  amountFormula?: string;
  /** Einheit der Menge (m², Stück, lfm, m³, kg, h, pauschal). */
  unit: string;
  /** @deprecated Legacy — nur noch für Hydration aus Altbestand. */
  totalArea?: number;
  /** @deprecated Legacy — nur noch für Hydration aus Altbestand. */
  netArea?: number;
  referencePoints: ReferencePoint[];
  openings: OpeningArea[];
  scale: number;
  includeOpenings: boolean;
  mengen: ManualMenge[];
  netAreaOverride?: number;
  /** @deprecated Legacy. */
  netAreaFormula?: string;
  /** @deprecated Legacy. */
  totalAreaFormula?: string;
  /** @deprecated Legacy. */
  openingCount?: number;
}

export const MEASUREMENT_UNITS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "m²", label: "m²" },
  { value: "Stück", label: "Stück" },
  { value: "lfm", label: "lfm (Laufmeter)" },
  { value: "m³", label: "m³" },
  { value: "kg", label: "kg" },
  { value: "h", label: "Stunden (h)" },
  { value: "pauschal", label: "pauschal" },
];

export const DEFAULT_MEASUREMENT_UNIT = "m²";

/** Liefert die effektive Menge: bevorzugt amount, sonst Legacy netArea. */
export function getMeasurementAmount(m: {
  amount?: number;
  netArea?: number;
} | null | undefined): number {
  if (!m) return 0;
  if (typeof m.amount === "number" && Number.isFinite(m.amount)) return m.amount;
  if (typeof m.netArea === "number" && Number.isFinite(m.netArea)) return m.netArea;
  return 0;
}

/** Liefert die effektive Einheit: bevorzugt unit, sonst Default m² (für Altbestand). */
export function getMeasurementUnit(m: { unit?: string } | null | undefined): string {
  return m?.unit && m.unit.length > 0 ? m.unit : DEFAULT_MEASUREMENT_UNIT;
}

interface AreaMeasurementProps {
  photo: { id: string; file: File; preview?: string; type: string };
  onMeasurementComplete?: (result: MeasurementResult) => void;
  highlightKey?: number;
  /** Server-Foto-ID — nötig, damit Markierungen am Foto persistiert werden können. */
  serverPhotoId?: number;
  /** Wird aufgerufen, sobald eine Markierung gespeichert wurde. Liefert die neue Vorschau-URL
   *  (mit Cache-Bust), damit der Konsument das Foto-Preview aktualisieren kann. */
  onAnnotationSaved?: (newPreviewUrl: string) => void;
  /** Neue Props (bevorzugt). */
  initialAmount?: number;
  initialAmountFormula?: string;
  initialUnit?: string;
  /** Legacy-Props — werden nur als Fallback für Altbestand genutzt. */
  initialNetArea?: number;
  initialNetAreaFormula?: string;
  /** @deprecated Wird ignoriert; Bruttofläche entfällt. */
  initialTotalArea?: number;
  /** @deprecated Wird ignoriert; Bruttofläche entfällt. */
  initialTotalAreaFormula?: string;
  /** @deprecated Wird ignoriert; Öffnungen entfallen. */
  initialOpeningCount?: number;
  /** @deprecated nur für Rückwärtskompatibilität, wird ignoriert */
  initialNetAreaOverride?: number;
}

const FORMULA_WHITELIST = /^[0-9+\-*/().,\s]+$/;

function evaluateFormula(expr: string): number | null {
  const trimmed = expr.trim();
  if (!trimmed) return null;
  if (!FORMULA_WHITELIST.test(trimmed)) return null;
  const sanitized = trimmed.replace(/,/g, ".");
  if (!/^[0-9+\-*/().\s]+$/.test(sanitized)) return null;
  try {
    // eslint-disable-next-line no-new-func
    const v = new Function(`"use strict"; return (${sanitized});`)();
    if (typeof v !== "number" || !Number.isFinite(v) || v < 0) return null;
    return v;
  } catch {
    return null;
  }
}

function formatGerman(v: number): string {
  return v.toFixed(2).replace(".", ",");
}

function initialFormula(
  formula: string | undefined,
  legacyFormula: string | undefined,
  amount: number | undefined,
  legacyAmount: number | undefined,
): string {
  if (formula && formula.trim().length > 0) return formula;
  if (legacyFormula && legacyFormula.trim().length > 0) return legacyFormula;
  if (typeof amount === "number" && amount > 0) return formatGerman(amount);
  if (typeof legacyAmount === "number" && legacyAmount > 0) return formatGerman(legacyAmount);
  return "";
}

export function AreaMeasurement({
  photo,
  onMeasurementComplete,
  highlightKey,
  serverPhotoId,
  onAnnotationSaved,
  initialAmount,
  initialAmountFormula,
  initialUnit,
  initialNetArea,
  initialNetAreaFormula,
}: AreaMeasurementProps) {
  const { toast } = useToast();
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [annotatorOpen, setAnnotatorOpen] = useState(false);
  const [annotationSaving, setAnnotationSaving] = useState(false);

  const canAnnotate =
    typeof serverPhotoId === "number" &&
    Number.isInteger(serverPhotoId) &&
    serverPhotoId > 0 &&
    !!photo.preview;

  const handleAnnotateClick = () => {
    if (!canAnnotate) return;
    setPhotoDialogOpen(false);
    setAnnotatorOpen(true);
  };

  const handleAnnotationSave = async (dataUrl: string) => {
    if (!canAnnotate || serverPhotoId == null) return;
    setAnnotationSaving(true);
    try {
      await PhotoApi.saveAnnotation(serverPhotoId, dataUrl);
      const bustedUrl = `${PhotoApi.getAnnotatedUrl(serverPhotoId)}?v=${Date.now()}`;
      onAnnotationSaved?.(bustedUrl);
      setAnnotatorOpen(false);
      toast({ title: "Markierung gespeichert" });
    } catch (err) {
      console.error("Annotation konnte nicht gespeichert werden:", err);
      toast({
        title: "Markierung konnte nicht gespeichert werden",
        description: err instanceof Error ? err.message : undefined,
        variant: "destructive",
      });
    } finally {
      setAnnotationSaving(false);
    }
  };

  const [formula, setFormula] = useState<string>(() =>
    initialFormula(initialAmountFormula, initialNetAreaFormula, initialAmount, initialNetArea),
  );
  const [unit, setUnit] = useState<string>(
    () => (initialUnit && initialUnit.length > 0 ? initialUnit : DEFAULT_MEASUREMENT_UNIT),
  );

  const [isHighlighted, setIsHighlighted] = useState(false);
  useEffect(() => {
    if (!highlightKey) return;
    setIsHighlighted(true);
    const t = setTimeout(() => setIsHighlighted(false), 1500);
    return () => clearTimeout(t);
  }, [highlightKey]);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const amountVal = evaluateFormula(formula);

  const scheduleEmit = (nextFormula: string, nextUnit: string) => {
    if (!onMeasurementComplete) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const n = evaluateFormula(nextFormula);
      if (n === null) return;
      onMeasurementComplete({
        photoId: photo.id,
        amount: n,
        amountFormula: nextFormula.trim() ? nextFormula : undefined,
        unit: nextUnit,
        referencePoints: [],
        openings: [],
        scale: 0,
        includeOpenings: false,
        mengen: [],
      });
    }, 500);
  };

  const onFormulaChange = (v: string) => {
    setFormula(v);
    scheduleEmit(v, unit);
  };
  const onUnitChange = (v: string) => {
    setUnit(v);
    scheduleEmit(formula, v);
  };

  const amountDisplay = amountVal !== null ? formatGerman(amountVal) : "—";

  return (
    <Card
      className={`mb-4 transition-shadow ${isHighlighted ? "ring-2 ring-orange-400" : ""}`}
      data-testid={`area-measurement-${photo.id}`}
    >
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-3 text-base">
          {photo.preview ? (
            <Dialog open={photoDialogOpen} onOpenChange={setPhotoDialogOpen}>
              <DialogTrigger asChild>
                <button
                  type="button"
                  className="shrink-0 overflow-hidden rounded-md border border-border focus:outline-none focus:ring-2 focus:ring-ring"
                  aria-label="Foto vergrößern"
                  data-testid={`thumbnail-${photo.id}`}
                >
                  <img
                    src={photo.preview}
                    alt={photo.file?.name ?? "Foto"}
                    className="w-16 h-16 object-cover"
                  />
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-[95vw] sm:max-w-4xl border-0 bg-transparent p-0 shadow-none">
                <VisuallyHidden>
                  <DialogTitle>{photo.file?.name ?? "Foto"}</DialogTitle>
                  <DialogDescription>Vergrößerte Ansicht des Fotos</DialogDescription>
                </VisuallyHidden>
                <img
                  src={photo.preview}
                  alt={photo.file?.name ?? "Foto"}
                  className="w-full h-auto max-h-[85vh] object-contain rounded-md"
                />
                {canAnnotate && (
                  <div className="mt-3 flex justify-center">
                    <Button
                      type="button"
                      onClick={handleAnnotateClick}
                      className="bg-bauwerk-orange hover:bg-bauwerk-light-orange"
                      data-testid={`button-photo-annotate-${photo.id}`}
                    >
                      <Pencil className="w-4 h-4 mr-2" />
                      Markieren
                    </Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          ) : null}
          <span className="flex items-center gap-2 min-w-0">
            <Calculator className="w-4 h-4 shrink-0 text-teal-700" />
            <span className="truncate">Aufmaß: {photo.file?.name ?? "Foto"}</span>
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label htmlFor={`amount-result-${photo.id}`}>Menge</Label>
            <Input
              id={`amount-result-${photo.id}`}
              type="text"
              readOnly
              value={amountDisplay}
              className="bg-gray-50 font-medium"
              data-testid={`amount-result-${photo.id}`}
            />
          </div>
          <div>
            <Label htmlFor={`amount-formula-${photo.id}`}>Berechnung (optional)</Label>
            <Input
              id={`amount-formula-${photo.id}`}
              type="text"
              inputMode="text"
              placeholder="z. B. 7,0*1,5"
              value={formula}
              onChange={(e) => onFormulaChange(e.target.value)}
              data-testid={`amount-formula-${photo.id}`}
            />
          </div>
          <div>
            <Label htmlFor={`amount-unit-${photo.id}`}>Einheit</Label>
            <Select value={unit} onValueChange={onUnitChange}>
              <SelectTrigger
                id={`amount-unit-${photo.id}`}
                data-testid={`amount-unit-${photo.id}`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MEASUREMENT_UNITS.map((u) => (
                  <SelectItem key={u.value} value={u.value}>
                    {u.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <p className="text-xs text-gray-500">
          Erlaubte Zeichen in der Berechnung: Ziffern, Komma oder Punkt, + − × ÷, Klammern.
          Beispiel: <code>3*4 + 1,5*2 - 1*1</code>
        </p>
      </CardContent>
      {annotatorOpen && photo.preview && (
        <PhotoAnnotator
          imageSrc={photo.preview}
          saving={annotationSaving}
          onCancel={() => {
            if (!annotationSaving) setAnnotatorOpen(false);
          }}
          onSave={handleAnnotationSave}
        />
      )}
    </Card>
  );
}
